/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Button from '@mui/material/Button';
import { Message } from '@twilio/conversations';
import throttle from 'lodash.throttle';
import { styled } from '@mui/material/styles';

const OuterContainer = styled('div')({
  minHeight: 0,
  flex: 1,
  position: 'relative',
});

const InnerScrollContainer = styled('div')({
  height: '100%',
  overflowY: 'auto',
  padding: '0 1.2em 0',
});

const MessageListContainer = styled('div')({
  overflowY: 'auto',
  flex: '1',
  paddingBottom: '1em',
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'showButton',
})<{ showButton?: boolean }>(({ showButton }) => ({
  position: 'absolute',
  bottom: '14px',
  right: '2em',
  zIndex: 100,
  padding: '0.5em 0.9em',
  visibility: 'hidden',
  opacity: 0,
  boxShadow: '0px 4px 16px rgba(18, 28, 45, 0.2)',
  transition: 'all 0.5s ease',
  ...(showButton && {
    visibility: 'visible',
    opacity: 1,
    bottom: '24px',
  }),
}));

interface MessageListScrollContainerProps {
  messages: Message[];
  children?: React.ReactNode;
}

/*
 * This component is a scrollable container that wraps around the 'MessageList' component.
 * The MessageList will ultimately grow taller than its container as it continues to receive
 * new messages, and users will need to have the ability to scroll up and down the chat window.
 * A "new message" button will be displayed when the user receives a new message, and is not scrolled
 * to the bottom. This button will be hidden if the user clicks on it, or manually scrolls
 * to the bottom. Otherwise, this component will auto-scroll to the bottom when a new message is
 * received, and the user is already scrolled to the bottom.
 *
 * Note that this component is tested with Cypress only.
 */
export function MessageListScrollContainer({ messages, children }: MessageListScrollContainerProps) {
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [messageNotificationCount, setMessageNotificationCount] = useState(0);
  const prevMessagesLength = useRef(messages.length);

  const scrollToBottom = () => {
    const innerScrollContainerEl = chatThreadRef.current;
    if (innerScrollContainerEl) {
      innerScrollContainerEl.scrollTop = innerScrollContainerEl.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
    const handleScroll = throttle(() => {
      const innerScrollContainerEl = chatThreadRef.current;
      if (!innerScrollContainerEl) return;

      const newIsScrolledToBottom =
        Math.abs(
          innerScrollContainerEl.clientHeight + innerScrollContainerEl.scrollTop - innerScrollContainerEl.scrollHeight
        ) < 1;

      setIsScrolledToBottom(newIsScrolledToBottom);
      if (newIsScrolledToBottom) {
        setShowButton(false);
      }
    }, 300);

    const innerScrollContainerEl = chatThreadRef.current;
    if (innerScrollContainerEl) {
      innerScrollContainerEl.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (innerScrollContainerEl) {
        innerScrollContainerEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const hasNewMessages = messages.length !== prevMessagesLength.current;

    if (hasNewMessages) {
      const numberOfNewMessages = messages.length - prevMessagesLength.current;
      prevMessagesLength.current = messages.length;

      if (isScrolledToBottom) {
        scrollToBottom();
      } else {
        setShowButton(true);
        setMessageNotificationCount(prevCount => (showButton ? prevCount + numberOfNewMessages : 1));
      }
    }
  }, [messages.length, isScrolledToBottom, showButton]);

  const handleClick = () => {
    const innerScrollContainerEl = chatThreadRef.current;
    if (innerScrollContainerEl) {
      innerScrollContainerEl.scrollTo({ top: innerScrollContainerEl.scrollHeight, behavior: 'smooth' });
    }
    setShowButton(false);
  };

  return (
    <OuterContainer>
      <InnerScrollContainer ref={chatThreadRef} data-cy-message-list-inner-scroll>
        <MessageListContainer>
          {children}
          <StyledButton
            showButton={showButton}
            onClick={handleClick}
            startIcon={<ArrowDownwardIcon />}
            color="primary"
            variant="contained"
            data-cy-new-message-button
          >
            {messageNotificationCount} new message
            {messageNotificationCount > 1 && 's'}
          </StyledButton>
        </MessageListContainer>
      </InnerScrollContainer>
    </OuterContainer>
  );
}

export default MessageListScrollContainer;
